import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useApi } from '../hooks/useApi';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Shield, TrendingUp, Calculator, ArrowRight, AlertCircle, Activity, Target, RefreshCw, Download, BarChart3 } from 'lucide-react';
import { RISK_CATEGORIES, formatCurrency } from '../utils/constants';

const Dashboard = () => {
  const { user } = useAuth();
  const { get } = useApi();
  const [riskProfile, setRiskProfile] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [sipSchedule, setSipSchedule] = useState(null);
  const [rebalance, setRebalance] = useState(null);
  const [modelInfo, setModelInfo] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [riskRes, portRes, sipRes, rebalRes, modelRes] = await Promise.allSettled([
          get('/risk/profile'),
          get('/portfolio'),
          get('/sip'),
          get('/portfolio/rebalance-check'),
          get('/ml/model-info'),
        ]);
        if (riskRes.status === 'fulfilled') setRiskProfile(riskRes.value);
        if (portRes.status === 'fulfilled') setPortfolio(portRes.value);
        if (sipRes.status === 'fulfilled') setSipSchedule(sipRes.value);
        if (rebalRes.status === 'fulfilled') setRebalance(rebalRes.value);
        if (modelRes.status === 'fulfilled') setModelInfo(modelRes.value);
      } catch {
        // Some may 404
      } finally {
        setLoaded(true);
      }
    };
    loadData();
  }, [get]);

  if (!loaded) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const categoryInfo = riskProfile ? RISK_CATEGORIES[riskProfile.riskCategory] : null;

  const breakdownData = riskProfile?.breakdown ? [
    { name: 'Preferences', score: riskProfile.breakdown.questionnaireScore },
    { name: 'Age', score: riskProfile.breakdown.ageScore },
    { name: 'Income', score: riskProfile.breakdown.incomeScore },
    { name: 'Expenses', score: riskProfile.breakdown.expenseScore },
    { name: 'Horizon', score: riskProfile.breakdown.horizonScore },
  ] : [];

  const handleDownloadReport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/portfolio/report`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Algodhan_Report.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Failed to download report');
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Welcome */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Welcome, {user?.name}! 👋</h1>
          <p className="text-gray-500 mt-1">Here's your financial overview</p>
        </div>
        <button onClick={handleDownloadReport} className="btn-secondary flex items-center gap-2 text-sm">
          <Download className="h-4 w-4" /> PDF Report
        </button>
      </div>

      {/* Top Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="card">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="h-5 w-5 text-green-600" />
            <span className="text-sm font-medium text-gray-600">Risk Profile</span>
          </div>
          {riskProfile ? (
            <>
              <p className="text-2xl font-bold" style={{ color: categoryInfo?.color }}>
                {categoryInfo?.emoji} {riskProfile.riskScore}
              </p>
              <p className="text-xs text-gray-500">{categoryInfo?.label}</p>
            </>
          ) : (
            <Link to="/risk-assessment" className="text-primary-600 text-sm hover:underline flex items-center gap-1">
              Take Assessment <ArrowRight className="h-3 w-3" />
            </Link>
          )}
        </div>

        <div className="card">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="h-5 w-5 text-blue-600" />
            <span className="text-sm font-medium text-gray-600">Portfolio</span>
          </div>
          {portfolio ? (
            <>
              <p className="text-2xl font-bold text-blue-600">{portfolio.allocations?.length || 0} Holdings</p>
              <p className="text-xs text-gray-500">Return: {((portfolio.metrics?.expectedReturn || 0) * 100).toFixed(1)}% | Sharpe: {(portfolio.metrics?.sharpeRatio || 0).toFixed(2)}</p>
            </>
          ) : (
            <Link to="/portfolio" className="text-primary-600 text-sm hover:underline flex items-center gap-1">
              Generate <ArrowRight className="h-3 w-3" />
            </Link>
          )}
        </div>

        <div className="card">
          <div className="flex items-center gap-2 mb-2">
            <Calculator className="h-5 w-5 text-purple-600" />
            <span className="text-sm font-medium text-gray-600">Monthly SIP</span>
          </div>
          {sipSchedule ? (
            <>
              <p className="text-2xl font-bold text-purple-600">{formatCurrency(sipSchedule.monthlyAmount)}</p>
              <p className="text-xs text-gray-500">Corpus: {formatCurrency(sipSchedule.projectedCorpus)}</p>
            </>
          ) : (
            <Link to="/sip-planner" className="text-primary-600 text-sm hover:underline flex items-center gap-1">
              Start SIP <ArrowRight className="h-3 w-3" />
            </Link>
          )}
        </div>

        <div className="card">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="h-5 w-5 text-orange-600" />
            <span className="text-sm font-medium text-gray-600">AI Engine</span>
          </div>
          {modelInfo ? (
            <>
              <p className="text-2xl font-bold text-green-600">Active</p>
              <p className="text-xs text-gray-500">Recommendations engine online</p>
            </>
          ) : (
            <p className="text-xs text-gray-400">Service offline</p>
          )}
        </div>
      </div>

      {/* Rebalancing Alert */}
      {rebalance?.needsRebalancing && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
          <div>
            <p className="font-semibold text-yellow-800">Rebalancing Recommended</p>
            <p className="text-sm text-yellow-700 mt-1">
              {rebalance.alerts.map(a => `${a.asset}: ${a.drift > 0 ? '+' : ''}${a.drift}% drift`).join(' | ')}
            </p>
            <Link to="/portfolio" className="text-yellow-800 underline text-sm mt-1 inline-block">
              Regenerate Portfolio →
            </Link>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Risk Breakdown Chart */}
        {breakdownData.length > 0 && (
          <div className="card">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary-600" /> Risk Factor Breakdown
            </h3>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={breakdownData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" domain={[0, 100]} />
                  <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value) => [`${value}/100`, 'Score']} />
                  <Bar dataKey="score" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Asset Mix */}
        {portfolio?.assetMix && (
          <div className="card">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Target className="h-5 w-5 text-primary-600" /> Asset Allocation
            </h3>
            <div className="space-y-3">
              {[
                { label: 'Equity', value: portfolio.assetMix.equity, color: 'bg-blue-500' },
                { label: 'Debt', value: portfolio.assetMix.debt, color: 'bg-green-500' },
                { label: 'Gold', value: portfolio.assetMix.gold, color: 'bg-yellow-500' },
              ].filter(a => a.value > 0).map((asset) => (
                <div key={asset.label}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium text-gray-700">{asset.label}</span>
                    <span className="text-gray-500">{asset.value}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div className={`${asset.color} h-3 rounded-full transition-all duration-700`} style={{ width: `${asset.value}%` }} />
                  </div>
                </div>
              ))}
            </div>
            {portfolio.totalInvestment > 0 && (
              <p className="mt-4 text-sm text-gray-500">
                Total Investment: <span className="font-semibold text-gray-800">{formatCurrency(portfolio.totalInvestment)}</span>
              </p>
            )}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="card">
        <h3 className="font-semibold text-gray-800 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {[
            { to: '/risk-assessment', label: 'Risk Quiz', icon: Shield, color: 'bg-green-50 text-green-600' },
            { to: '/portfolio', label: 'Portfolio', icon: TrendingUp, color: 'bg-blue-50 text-blue-600' },
            { to: '/sip-planner', label: 'SIP Planner', icon: Calculator, color: 'bg-purple-50 text-purple-600' },
            { to: '/corpus-projection', label: 'Projection', icon: Target, color: 'bg-orange-50 text-orange-600' },
            { to: '/market-data', label: 'Market Data', icon: Activity, color: 'bg-red-50 text-red-600' },
            { to: '/backtest', label: 'Backtest', icon: RefreshCw, color: 'bg-teal-50 text-teal-600' },
          ].map((action, idx) => {
            const Icon = action.icon;
            return (
              <Link key={idx} to={action.to}
                className="flex flex-col items-center gap-2 p-4 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors"
              >
                <div className={`p-2 rounded-lg ${action.color}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <span className="text-xs font-medium text-gray-700">{action.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
